var _a;
import api from 'axios';
import applyCaseMiddleware from 'axios-case-converter';
import qs from 'qs';
// On API Calls:
// - Convert camelCase to snake_case for typescript -> rails standards
// - Convert arrays to compatible rails array syntax
/* eslint-disable */
const apiClient = applyCaseMiddleware(api.create({
    baseURL: window.location.origin,
    paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: 'brackets' });
    },
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': ((_a = document.querySelector('meta[name="csrf-token"]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content')) || ''
    }
}));
// ---------------------------------------------------------------------------
// Cross-origin override for the standalone chat widget bundle.
//
// When the widget is loaded by a non-Rails host (e.g. the Angular Technician
// Portal) `window.location.origin` is NOT the tenant's Rails origin, so the
// default `baseURL` above points at the wrong server. `configureApiClient`
// lets `mountChatWidget()` redirect every subsequent request to the correct
// tenant host AND inject `auth_token` / `username` query params on every
// request, which the Rails tech-portal endpoints already accept as an
// alternative to cookie-based session auth.
//
// Call it again with `null` baseUrl / authParams to clear. The function
// returns a restore closure that `mount.ts` wires into the widget's cleanup
// callback so a host that unmounts the widget leaves the client state as
// it found it.
// ---------------------------------------------------------------------------
let apiClientAuthParams = null;
let apiClientPathRewrites = [];
/**
 * Prefix rewrites applied when the widget is mounted cross-origin from the
 * technician portal. The portal authenticates with `auth_token` + `username`,
 * not a Devise session, and `/api/internal/*` is not in the CORS allowlist.
 */
export const TECHNICIAN_PORTAL_PATH_REWRITES = [
    { prefix: '/api/v1/ai/', replacement: '/api/technician_portal/ai/' },
    { prefix: '/api/internal/ai/', replacement: '/api/technician_portal/ai/' },
    { prefix: '/api/internal/buildings/', replacement: '/api/technician_portal/buildings/' }
];
/**
 * Apply the active base URL, path rewrites and auth query params to a Rails
 * path. Same-origin (the back office) stays a relative URL so the Devise
 * cookie is sent. Cross-origin (the technician portal) becomes an absolute
 * URL on the tenant Rails host; callers must not send credentials, because
 * the CORS allowlist is `*` and auth travels in the query string.
 */
export function resolveApiUrl(path) {
    var _a;
    let url = path;
    for (const rewrite of apiClientPathRewrites) {
        if (url.startsWith(rewrite.prefix)) {
            url = rewrite.replacement + url.slice(rewrite.prefix.length);
            break;
        }
    }
    const base = ((_a = apiClient.defaults.baseURL) !== null && _a !== void 0 ? _a : '').replace(/\/$/, '');
    const origin = typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : '';
    const crossOrigin = /^https?:\/\//.test(base) && base !== origin;
    let resolved = crossOrigin ? new URL(url, `${base}/`).toString() : url;
    if (apiClientAuthParams) {
        const parsed = new URL(resolved, origin || 'http://localhost');
        for (const [key, value] of Object.entries(apiClientAuthParams)) {
            parsed.searchParams.set(key, value);
        }
        resolved = crossOrigin ? parsed.toString() : `${parsed.pathname}${parsed.search}`;
    }
    return { url: resolved, crossOrigin };
}
const authParamInterceptorId = apiClient.interceptors.request.use((config) => {
    var _a;
    if (apiClientAuthParams) {
        config.params = Object.assign(Object.assign({}, ((_a = config.params) !== null && _a !== void 0 ? _a : {})), apiClientAuthParams);
    }
    if (apiClientPathRewrites.length > 0 && typeof config.url === 'string') {
        for (const rewrite of apiClientPathRewrites) {
            if (config.url.startsWith(rewrite.prefix)) {
                config.url = rewrite.replacement + config.url.slice(rewrite.prefix.length);
                break;
            }
        }
    }
    return config;
});
// Hold a reference so a future test can eject the interceptor if needed.
apiClient.__authInterceptorId = authParamInterceptorId;
export function configureApiClient(options) {
    var _a, _b, _c;
    const previousBaseUrl = apiClient.defaults.baseURL;
    const previousAuthParams = apiClientAuthParams;
    const previousPathRewrites = apiClientPathRewrites;
    if (options.baseUrl !== undefined) {
        apiClient.defaults.baseURL = (_a = options.baseUrl) !== null && _a !== void 0 ? _a : window.location.origin;
    }
    if (options.authParams !== undefined) {
        apiClientAuthParams = (_b = options.authParams) !== null && _b !== void 0 ? _b : null;
    }
    if (options.pathRewrites !== undefined) {
        apiClientPathRewrites = (_c = options.pathRewrites) !== null && _c !== void 0 ? _c : [];
    }
    return function restoreApiClient() {
        apiClient.defaults.baseURL = previousBaseUrl;
        apiClientAuthParams = previousAuthParams;
        apiClientPathRewrites = previousPathRewrites;
    };
}
export default apiClient;
